/**
 * UK mobile numbers. One account per phone number (CLAUDE.md §4), so every
 * number is stored in one canonical form: E.164, e.g. +447700900123.
 * Only UK mobiles (07xxx) are accepted at launch; landlines and other
 * countries are refused with a clear message.
 */
export class PhoneError extends Error {
  override name = 'PhoneError';
}

/** "07700 900123", "+44 7700 900123", "00447700900123" -> "+447700900123". Returns null if not a UK mobile. */
export function normaliseUkMobile(input: string): string | null {
  if (typeof input !== 'string') return null;
  let digits = input.replace(/[^\d+]/g, '');
  if (digits.startsWith('00')) digits = '+' + digits.slice(2);
  if (digits.startsWith('+44')) digits = '0' + digits.slice(3);
  else if (digits.startsWith('44') && digits.length === 12) digits = '0' + digits.slice(2);
  // Typed next to the app's "+44" box, as its placeholder shows: 7700 900123 (no leading 0).
  else if (/^7\d{9}$/.test(digits)) digits = '0' + digits;
  if (digits.startsWith('+')) return null;
  if (!/^07\d{9}$/.test(digits)) return null;
  return '+44' + digits.slice(1);
}

export function assertUkMobile(input: string): string {
  const n = normaliseUkMobile(input);
  if (!n) throw new PhoneError('Enter a UK mobile number, for example 07700 900123');
  return n;
}

/** "+447700900123" -> "07700 900123" */
export function formatUkPhone(e164: string): string {
  if (!e164.startsWith('+44')) return e164;
  const n = '0' + e164.slice(3);
  return `${n.slice(0, 5)} ${n.slice(5)}`;
}

/** "+447700900123" -> "07700 •••123" (for receipts and the account screen). */
export function maskPhone(e164: string): string {
  const f = formatUkPhone(e164);
  return f.length >= 6 ? f.slice(0, 6) + '•••' + f.slice(-3) : f;
}
